import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CUSTOMER_TYPES, STATUS_OPTIONS } from "@/lib/constants";
import { Search, Plus, Pencil, Trash2, Users2 } from "lucide-react";

const EMPTY = {
  customer_type: "individual",
  legal_name: "",
  contact_name: "",
  phone: "",
  email: "",
  address: "",
  rfc: "",
  notes: "",
  status: "active",
};

export default function Clientes() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  async function fetchList() {
    if (!canQuery) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/customers", {
        params: { ...tenantParam, search: search || undefined },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTenantId, isSuperAdmin]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setSheetOpen(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ ...EMPTY, ...c });
    setSheetOpen(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/customers", form, { params: tenantParam });
        toast.success("Cliente creado");
      }
      setSheetOpen(false);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/customers/${confirmDelete.id}`);
      toast.success("Cliente eliminado");
      setConfirmDelete(null);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-clientes">
      <PageHeader
        title="Clientes"
        description="Registra a las personas y empresas cuyos vehículos atenderás en el taller."
        actions={
          <Button
            className="btn-tenant"
            onClick={openCreate}
            disabled={!canQuery}
            data-testid="btn-new-cliente"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo cliente
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, teléfono, RFC…"
            className="pl-9 h-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-clientes"
          />
        </div>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="clientes-count">
          {items.length} registrado{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Aún no hay clientes" : "Selecciona un tenant"}
            description={
              canQuery
                ? "Crea el primer cliente para empezar a registrar vehículos y futuras órdenes de servicio."
                : "Como Super Admin VRIntegrales, primero elige un tenant desde la sección de Tenants."
            }
            icon={Users2}
            action={
              canQuery && (
                <Button className="btn-tenant" onClick={openCreate} data-testid="empty-new-cliente">
                  <Plus className="h-4 w-4 mr-2" /> Crear cliente
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Nombre / Razón social</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>RFC</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="tbl-row" data-testid={`row-cliente-${c.id}`}>
                  <TableCell>
                    <div className="font-medium">{c.legal_name}</div>
                    {c.contact_name && (
                      <div className="text-xs text-muted-foreground">{c.contact_name}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {CUSTOMER_TYPES.find((t) => t.value === c.customer_type)?.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{c.phone || "—"}</div>
                    <div className="text-xs text-muted-foreground">{c.email || ""}</div>
                  </TableCell>
                  <TableCell className="mono text-xs">{c.rfc || "—"}</TableCell>
                  <TableCell>
                    <Badge
                      className={c.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}
                    >
                      {c.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(c)}
                      data-testid={`btn-edit-cliente-${c.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(c)}
                      data-testid={`btn-delete-cliente-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Form Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-cliente">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</SheetTitle>
            <SheetDescription>
              Datos básicos del cliente. Podrás vincularle vehículos después.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de cliente</Label>
                <Select
                  value={form.customer_type}
                  onValueChange={(v) => setForm({ ...form, customer_type: v })}
                >
                  <SelectTrigger data-testid="cliente-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estatus</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger data-testid="cliente-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre / Razón social *</Label>
              <Input
                required
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                data-testid="cliente-legal-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre de contacto</Label>
              <Input
                value={form.contact_name || ""}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="cliente-contact-name"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="cliente-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="cliente-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>RFC</Label>
              <Input
                value={form.rfc || ""}
                onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                data-testid="cliente-rfc"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={form.address || ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="cliente-address"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                rows={3}
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="cliente-notes"
              />
            </div>
            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSheetOpen(false)}
                data-testid="cliente-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-tenant"
                data-testid="cliente-save"
              >
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear cliente"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cliente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres eliminar a <b>{confirmDelete?.legal_name}</b>? Esta acción no se puede deshacer.
              Si tiene vehículos asociados, el sistema te pedirá removerlos primero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-cliente"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
